import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';

const makeMat = (color, emissive = color, opacity = 1) => new THREE.MeshStandardMaterial({
  color,
  emissive,
  emissiveIntensity: 0.18,
  roughness: 0.42,
  metalness: 0.24,
  transparent: opacity < 1,
  opacity,
});

function addBox(parent, size, position, material, radius = false) {
  const geometry = new THREE.BoxGeometry(...size);
  const mesh = new THREE.Mesh(geometry, material);
  mesh.position.set(...position);
  parent.add(mesh);

  if (radius) {
    const edges = new THREE.LineSegments(
      new THREE.EdgesGeometry(geometry),
      new THREE.LineBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.2 })
    );
    mesh.add(edges);
  }

  return { mesh, geometry };
}

function addLine(parent, start, end, material) {
  const geometry = new THREE.BufferGeometry().setFromPoints([
    new THREE.Vector3(...start),
    new THREE.Vector3(...end),
  ]);
  const line = new THREE.Line(geometry, material);
  parent.add(line);
  return geometry;
}

function HeroThreeScene() {
  const mountRef = useRef(null);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return undefined;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(42, 1, 0.1, 100);
    camera.position.set(4.4, 3.1, 7.4);
    camera.lookAt(0, 0.1, 0);

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.6));
    renderer.setClearColor(0x000000, 0);
    mount.appendChild(renderer.domElement);

    const root = new THREE.Group();
    root.rotation.y = -0.18;
    scene.add(root);

    scene.add(new THREE.AmbientLight(0xffffff, 0.92));

    const keyLight = new THREE.DirectionalLight(0xffffff, 2.2);
    keyLight.position.set(3.5, 5, 4);
    scene.add(keyLight);

    const blueLight = new THREE.PointLight(0x38d5ff, 2.7, 12);
    blueLight.position.set(-3, 2, 4);
    scene.add(blueLight);

    const greenLight = new THREE.PointLight(0x7cffbf, 1.9, 12);
    greenLight.position.set(3, -1.6, 3);
    scene.add(greenLight);

    const materials = {
      blue: makeMat(0x0796c9, 0x026f9f),
      green: makeMat(0x28c987, 0x0b7f55),
      amber: makeMat(0xf6c76b, 0x9a5f13),
      dark: makeMat(0x0f1724, 0x020711),
      glass: makeMat(0x183148, 0x08111f, 0.72),
      line: new THREE.LineBasicMaterial({ color: 0x38d5ff, transparent: true, opacity: 0.58 }),
      grid: new THREE.LineBasicMaterial({ color: 0x2c556d, transparent: true, opacity: 0.24 }),
    };

    const disposables = Object.values(materials);
    const animated = [];

    const grid = new THREE.GridHelper(7, 14, 0x38d5ff, 0x183148);
    grid.position.y = -1.85;
    grid.material.transparent = true;
    grid.material.opacity = 0.18;
    root.add(grid);

    const laptop = new THREE.Group();
    laptop.position.set(-1.45, 0.05, 0);
    laptop.rotation.y = 0.32;
    root.add(laptop);

    [
      addBox(laptop, [2.45, 0.1, 1.42], [0, -0.7, 0], materials.dark, true),
      addBox(laptop, [2.25, 1.35, 0.12], [0, 0.05, -0.62], materials.glass, true),
      addBox(laptop, [1.82, 0.92, 0.04], [0, 0.08, -0.55], materials.blue),
    ].forEach(({ geometry }) => disposables.push(geometry));

    const codeLineGeometry = new THREE.BoxGeometry(0.78, 0.035, 0.03);
    disposables.push(codeLineGeometry);
    for (let index = 0; index < 6; index += 1) {
      const line = new THREE.Mesh(codeLineGeometry, index % 2 ? materials.green : materials.amber);
      line.position.set(-0.45 + (index % 3) * 0.36, 0.38 - index * 0.15, -0.5);
      line.scale.x = index % 2 ? 0.72 : 1;
      laptop.add(line);
    }

    const phone = new THREE.Group();
    phone.position.set(1.75, -0.18, 0.62);
    phone.rotation.y = -0.34;
    root.add(phone);

    [
      addBox(phone, [0.7, 1.45, 0.14], [0, 0, 0], materials.dark, true),
      addBox(phone, [0.52, 1.08, 0.035], [0, 0, 0.09], materials.green),
    ].forEach(({ geometry }) => disposables.push(geometry));

    const server = new THREE.Group();
    server.position.set(1.35, 0.72, -1.08);
    root.add(server);

    for (let index = 0; index < 3; index += 1) {
      const { mesh, geometry } = addBox(server, [1.02, 0.28, 0.62], [0, index * 0.36, 0], materials.glass, true);
      disposables.push(geometry);
      const indicator = new THREE.Mesh(new THREE.SphereGeometry(0.04, 12, 8), index === 1 ? materials.green : materials.blue);
      indicator.position.set(-0.38, index * 0.36, 0.34);
      server.add(indicator);
      disposables.push(indicator.geometry);
      animated.push(mesh);
    }

    const database = new THREE.Group();
    database.position.set(-0.25, -1.22, 0.82);
    root.add(database);

    const dbTop = new THREE.Mesh(new THREE.CylinderGeometry(0.62, 0.62, 0.16, 48), materials.amber);
    const dbBody = new THREE.Mesh(new THREE.CylinderGeometry(0.62, 0.62, 0.62, 48, 1, true), materials.glass);
    dbTop.position.y = 0.34;
    dbBody.position.y = 0;
    database.add(dbTop, dbBody);
    disposables.push(dbTop.geometry, dbBody.geometry);

    const cloud = new THREE.Group();
    cloud.position.set(0.1, 1.7, -0.75);
    root.add(cloud);
    [
      [-0.42, 0, 0, 0.34],
      [0, 0.14, 0, 0.46],
      [0.42, 0, 0, 0.34],
      [0.05, -0.08, 0, 0.36],
    ].forEach(([x, y, z, radius]) => {
      const sphere = new THREE.Mesh(new THREE.SphereGeometry(radius, 28, 18), materials.blue);
      sphere.position.set(x, y, z);
      cloud.add(sphere);
      disposables.push(sphere.geometry);
      animated.push(sphere);
    });

    [
      addLine(root, [-0.2, 0.34, -0.2], [1.05, 0.96, -0.84], materials.line),
      addLine(root, [0.8, -0.08, 0.48], [1.36, -0.12, 0.62], materials.line),
      addLine(root, [-0.55, -0.68, 0.2], [-0.25, -0.85, 0.68], materials.line),
      addLine(root, [0, 1.28, -0.55], [0.05, 1.48, -0.72], materials.line),
    ].forEach((geometry) => disposables.push(geometry));

    const resize = () => {
      const { width, height } = mount.getBoundingClientRect();
      const nextWidth = Math.max(1, width);
      const nextHeight = Math.max(1, height);
      renderer.setSize(nextWidth, nextHeight, false);
      camera.aspect = nextWidth / nextHeight;
      camera.updateProjectionMatrix();
      root.scale.setScalar(nextWidth < 460 ? 0.78 : 1);
    };

    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(mount);
    resize();

    let frame = 0;
    let animationId;

    const animate = () => {
      frame += 0.01;
      if (!prefersReducedMotion) {
        root.rotation.y = -0.18 + Math.sin(frame * 0.7) * 0.04;
        phone.position.y = -0.18 + Math.sin(frame * 1.2) * 0.04;
        cloud.position.y = 1.7 + Math.sin(frame * 0.9) * 0.05;
        animated.forEach((item, index) => {
          item.rotation.y = Math.sin(frame + index) * 0.025;
        });
      }

      renderer.render(scene, camera);
      animationId = window.requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.cancelAnimationFrame(animationId);
      resizeObserver.disconnect();
      mount.removeChild(renderer.domElement);
      disposables.forEach((item) => item.dispose());
      renderer.dispose();
    };
  }, []);

  return (
    <div className="portfolioThreeScene" ref={mountRef} aria-label="3D software delivery model">
      <div className="portfolioThreeScene__badge portfolioThreeScene__badge--web">Web App</div>
      <div className="portfolioThreeScene__badge portfolioThreeScene__badge--mobile">Mobile App</div>
      <div className="portfolioThreeScene__badge portfolioThreeScene__badge--api">APIs</div>
      <div className="portfolioThreeScene__badge portfolioThreeScene__badge--data">Database</div>
      <div className="portfolioThreeScene__badge portfolioThreeScene__badge--cloud">Cloud</div>
      <div className="portfolioThreeScene__status">
        <span>3D Product System</span>
        <strong>Web + Mobile + Backend + Cloud</strong>
      </div>
    </div>
  );
}

export default HeroThreeScene;
